import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Modal, ModalActions, ModalContent, ModalTitleInverse, useSnackbar } from '@c2fo/liquidity';
import Skeleton from '@/enterprise/components/Skeleton';
import { useConfirmAgreementsWhenRequired } from '@/enterprise/data/useAgreements';
import {
  useGlobalPriceDiscoveryOffer,
  useGlobalVariableRateOffer,
  useGlobalFixedRateOffer,
} from '@/enterprise/data/useGlobalOffer';
import useGroupByPricingType from '@/enterprise/data/useGroupByPricingType';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useReporting } from '@/reporting';
import useDataDog from '@/reporting/DataDog';
import { useIntercom } from '@/reporting/Intercom';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import useExchangeRates from '@/shared/data/useExchangeRates';
import useRestrictions from '@/utils/useRestrictions';
import { Agreement } from '../agreements/Agreement';
import { GlobalParticipationTypes } from '../takerMarketTabs/components/TabActionsDropdown';
import { trackParticipationMarketEvents } from './utils';

interface SetGlobalParticipationProps {
  open: boolean;
  onClose: () => void;
  takerMarkets: TakerMarket[];
}

const SetGlobalParticipationComponent = ({ onClose, takerMarkets }: Omit<SetGlobalParticipationProps, 'open'>) => {
  const { t } = useTranslation();
  const { addError } = useDataDog();
  const showSnackbar = useSnackbar();
  const { trackEnterpriseEvent } = useReporting();
  const { data: exchangeRates } = useExchangeRates();
  const [isLoading, setIsLoading] = useState(false);
  const { groupByPricingType } = useGroupByPricingType();
  const takerMarketsGroups = groupByPricingType(takerMarkets);
  const { BENCHMARK, FIXED, NAME_YOUR_RATE } = takerMarketsGroups;
  const { mutateAsync: submitGlobalPriceDiscoveryParticipation } = useGlobalPriceDiscoveryOffer(
    NAME_YOUR_RATE.takerMarkets
  );
  const { mutateAsync: submitGlobalVariableRateParticipation } = useGlobalVariableRateOffer(BENCHMARK.takerMarkets);
  const { mutateAsync: submitGlobalFixedRateParticipation } = useGlobalFixedRateOffer(FIXED.takerMarkets);
  const { getRestrictions } = useRestrictions();
  const { canEditOffers } = getRestrictions(takerMarkets);
  const { validateAndConfirmAgreementsConditionally } = useConfirmAgreementsWhenRequired(takerMarkets);
  const [isAgreementsChecked, setIsAgreementsChecked] = useState(false);
  const [isAgreementsErrorNotChecked, setIsAgreementsErrorNotChecked] = useState(false);
  const { triggerNPSSurvey } = useIntercom();

  const onSubmit = async () => {
    if (!canEditOffers) {
      return;
    }

    setIsLoading(true);

    try {
      await validateAndConfirmAgreementsConditionally({ isAgreementsChecked });
    } catch {
      setIsAgreementsErrorNotChecked(true);
      setIsLoading(false);
      return;
    }

    const submitValues = { isEnabled: true };
    const submissionBatchId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    const succeededGroups: { type: GlobalParticipationTypes; groupTakerMarkets: TakerMarket[] }[] = [];

    const handleSuccessfulSubmission = (type: GlobalParticipationTypes, groupTakerMarkets: TakerMarket[]) => {
      succeededGroups.push({ type, groupTakerMarkets });
      trackParticipationMarketEvents({
        takerMarkets: groupTakerMarkets,
        pricingType: type,
        isEnabled: true,
        exchangeRates,
        submissionBatchId,
        trackEnterpriseEvent,
      });
    };

    try {
      const promises = [];

      if (NAME_YOUR_RATE.takerMarkets.length > 0) {
        promises.push(
          submitGlobalPriceDiscoveryParticipation(submitValues, {
            onSuccess: () => handleSuccessfulSubmission('NAME_YOUR_RATE', NAME_YOUR_RATE.takerMarkets),
          })
        );
      }

      if (BENCHMARK.takerMarkets.length > 0) {
        promises.push(
          submitGlobalVariableRateParticipation(submitValues, {
            onSuccess: () => handleSuccessfulSubmission('BENCHMARK', BENCHMARK.takerMarkets),
          })
        );
      }

      if (FIXED.takerMarkets.length > 0) {
        promises.push(
          submitGlobalFixedRateParticipation(submitValues, {
            onSuccess: () => handleSuccessfulSubmission('FIXED', FIXED.takerMarkets),
          })
        );
      }

      await Promise.all(promises);

      if (succeededGroups.length > 0) {
        showSnackbar({ message: t('buyerControls.participationTurnedOn') });
        trackEnterpriseEvent('global-participation::submitted', {
          isEnabled: true,
          submissionBatchId,
          NAME_YOUR_RATE: succeededGroups.find((g) => g.type === 'NAME_YOUR_RATE')?.groupTakerMarkets.length ?? 0,
          BENCHMARK: succeededGroups.find((g) => g.type === 'BENCHMARK')?.groupTakerMarkets.length ?? 0,
          FIXED: succeededGroups.find((g) => g.type === 'FIXED')?.groupTakerMarkets.length ?? 0,
        });
        onClose();
        triggerNPSSurvey();
      }
    } catch (error) {
      showSnackbar({ message: t('buyerControls.participationUpdateError') });
      setIsLoading(false);
      addError('Enterprise Buyer Participation On Error', {
        error,
        location: 'SetGlobalParticipation -> onSubmit',
        takerMarkets: takerMarkets.map((tm) => ({
          makerOrganizationUuid: tm.makerOrganizationUuid,
          marketUuid: tm.marketUuid,
          supplierDivisionUuid: tm.takerDivisionId,
        })),
      });
    }
  };

  return (
    <>
      <ModalContent>
        <div className="relative space-y-6 bg-white text-text-primary">
          {/* info */}
          <div className="rounded-md bg-canvas p-4 font-medium">{t('offer.participateInFutureClearsMessage')}</div>
          {/* understand info */}
          <div>
            <ul className="ml-4 list-disc space-y-2">
              <li>{t('allOfferDialogs.description2')}</li>
              <li>{t('allOfferDialogs.description3')}</li>
              <li>{t('offerSubmitDialog.disclaimer')}</li>
            </ul>
          </div>
          <Agreement
            takersMarkets={takerMarkets}
            onChange={(v) => {
              setIsAgreementsChecked(v);
              setIsAgreementsErrorNotChecked(false);
            }}
          />
          {isAgreementsErrorNotChecked && (
            <Alert type="error" variant="filled" description={t('agreements.error.checkbox')} full />
          )}
        </div>
      </ModalContent>
      <ModalActions>
        <Button onClick={onClose} variant="secondary">
          {t('core.cancel')}
        </Button>
        <Button onClick={onSubmit} variant="cta" disabled={isLoading} loading={isLoading}>
          {t('core.submit')}
        </Button>
      </ModalActions>
    </>
  );
};

const SetGlobalParticipation = (props: SetGlobalParticipationProps) => {
  const { t } = useTranslation();

  return (
    <Modal open={props.open} onClose={props.onClose} className="sm:w-[750px]">
      <ModalTitleInverse>{t('offer.setParticipation')}</ModalTitleInverse>
      <QueryBoundaries
        LoadingComponent={() => (
          <div className="space-y-4 rounded-md p-6">
            <div className="space-y-2">
              <Skeleton className="h-16 w-full opacity-60" shadow={false} />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-6 w-1/4 opacity-70" shadow={false} />
              <Skeleton className="h-10 w-full opacity-60" shadow={false} />
            </div>
            <div className="space-y-2 pt-2">
              <Skeleton className="h-6 w-1/2 opacity-70" shadow={false} />
              <Skeleton className="h-24 w-full opacity-60" shadow={false} />
            </div>
          </div>
        )}
      >
        <SetGlobalParticipationComponent {...props} />
      </QueryBoundaries>
    </Modal>
  );
};

export default SetGlobalParticipation;
